/**
 * Match schedule grouped by calendar day (local TZ) and stage / group.
 * Reads the `isoDate` and `localDateStamp` decorated onto each match by
 * `getData()` so no per-render date math is needed downstream.
 *
 * Memoised (issue #28): repeated calls with the same `matches` object
 * reference return the same precomputed tree. The cache is a `WeakMap`
 * keyed on the input reference, so stale results are garbage-collected
 * the moment a fresh poller tick produces a new `matches` payload.
 */

import { getLocalDateKey } from '$utils/dates';
import { group } from 'd3-array';

const getStageName = (match) =>
  match.matchInfo.series?.name ?? match.matchInfo.stage.name;

/**
 * Internal: build the schedule tree from scratch. Kept as a pure function
 * so the memo wrapper can stay tiny and the original behaviour is easy to
 * verify against history.
 */
const buildSchedule = (matches, order) => {
  const matchList = matches?.matches?.match;
  if (!Array.isArray(matchList) || matchList.length === 0) return [];

  const byIsoAsc = (a, b) => a.isoDate.localeCompare(b.isoDate);

  const nested = group(
    matchList,
    (m) => getLocalDateKey(m.isoDate),
    getStageName
  );

  const days = [...nested.entries()].map(([, stageMap]) => {
    const matchesOnDate = [...stageMap.entries()]
      .map(([stage, stageMatches]) => ({
        stage,
        matchesForStage: [...stageMatches].sort(byIsoAsc),
      }))
      .sort((a, b) =>
        a.matchesForStage[0].isoDate.localeCompare(b.matchesForStage[0].isoDate)
      );

    const sampleMatch = matchesOnDate[0].matchesForStage[0];
    return {
      isoDate: sampleMatch.isoDate,
      localDateStamp: sampleMatch.localDateStamp,
      matchesOnDate,
    };
  });

  const ascending = order === 'asc';
  days.sort((a, b) =>
    ascending ?
      a.isoDate.localeCompare(b.isoDate)
    : b.isoDate.localeCompare(a.isoDate)
  );

  return days;
};

/**
 * Per-`matches`-reference cache. Holds the most recent result keyed by
 * input reference; entries auto-GC when the input is replaced.
 *
 * The value remembers the `order` it was built with so toggling between
 * ascending / descending invalidates correctly without rebuilding both.
 *
 * @type {WeakMap<object, { order: string, tree: any[] }>}
 */
const cache = new WeakMap();

/**
 * @param {Object} matches
 * @param {'asc'|'desc'|'dec'} [order='desc'] - `'dec'` aliases `'desc'`.
 * @returns {Array<{
 *   isoDate: string,
 *   localDateStamp: ReturnType<typeof getDate>,
 *   matchesOnDate: Array<{ stage: string, matchesForStage: any[] }>,
 * }>}
 */
export default (matches, order = 'desc') => {
  // Bail out cleanly for falsy / primitive inputs — WeakMap would reject
  // them anyway, and the upstream behaviour was an empty array.
  if (!matches || typeof matches !== 'object') {
    return buildSchedule(matches, order);
  }

  const hit = cache.get(matches);
  if (hit && hit.order === order) return hit.tree;

  const tree = buildSchedule(matches, order);
  cache.set(matches, { order, tree });
  return tree;
};
